import { useMemo } from 'react';
import { FilterPickerConsumerProps } from './FilterPickerDynamicConsumer';
import useCompaniesContext from '../../../modules/companies/hooks/useCompaniesContext';
import useAccessRights2 from '../../../modules/users/hooks/useAccessRights2';
import useFilterPickerContext from '../hooks/useFilterPickerContext';
import { Company } from '../../../models/Company';
import { useTheme } from '@prio365/prio365-react-library';
import { PrioTheme } from '../../../theme/types';

interface CompanyFilterPickerConsumerProps extends FilterPickerConsumerProps {
  type?: 'all' | 'internal' | 'external';
}

const CompanyFilterPickerConsumer: React.FC<
  CompanyFilterPickerConsumerProps
> = (props) => {
  //#region ------------------------------ Defaults
  const { children, roles, type, parameterName } = props;

  const theme = useTheme<PrioTheme>();

  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { companies: _companies } = useCompaniesContext(true);

  const {
    checkOfficeRolesOfCompanyId,
    checkProjectRolesOfCompanyId,
    checkGlobalRoles,
  } = useAccessRights2();

  const typeToPickerType = useMemo(() => {
    switch (type) {
      case 'internal':
        return 'internalCompany';
      case 'external':
        return 'externalCompany';
      default:
        return 'company';
    }
  }, [type]);

  const pickerFilterFunction = useFilterPickerContext<Company>(
    parameterName ?? typeToPickerType
  );

  const companies = useMemo(() => {
    const companies = _companies.filter((company) => {
      const { companyType, fullName, fullName2, shortName } = company;
      if (!fullName && !fullName2 && !shortName) {
        return false;
      }
      switch (type) {
        case 'internal': {
          return companyType === 'internalCompany';
        }
        case 'external': {
          return companyType === 'externalCompany';
        }
        default: {
          return true;
        }
      }
    });
    if (
      roles.globalRoles.length > 0 &&
      checkGlobalRoles(
        roles.globalRoles.filter((role) => role !== 'globalEmployee')
      )
    ) {
      return companies;
    }
    if (roles.officeRoles.length > 0 && roles.projectRoles.length > 0) {
      return companies.filter(
        ({ companyId, companyType }) =>
          companyType === 'externalCompany' ||
          checkOfficeRolesOfCompanyId(roles.officeRoles, companyId) ||
          checkProjectRolesOfCompanyId(roles.projectRoles, companyId)
      );
    }
    return companies;
  }, [
    type,
    roles,
    _companies,
    checkOfficeRolesOfCompanyId,
    checkProjectRolesOfCompanyId,
    checkGlobalRoles,
  ]);

  const filteredCompanies = useMemo(() => {
    if (pickerFilterFunction) {
      return companies.filter(pickerFilterFunction);
    }
    return companies;
  }, [companies, pickerFilterFunction]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <>
      {children({
        options: filteredCompanies.map(
          ({ companyId, fullName, fullName2, shortName }) => {
            return {
              value: companyId,
              label: (
                <div>
                  <div>{fullName ?? fullName2}</div>
                  {shortName && (
                    <div
                      style={{
                        color: theme.colors.application.typography.muted,
                        fontSize: theme.font.fontSize.extraSmall,
                      }}
                    >
                      {shortName}
                    </div>
                  )}
                </div>
              ),

              searchValue: `${fullName} ${shortName} ${companyId}`,
            };
          }
        ),
      })}
    </>
  );
};

export default CompanyFilterPickerConsumer;
